import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, Navigate } from "react-router-dom";
import Navbar from "../../../ui/Navbar";
import { AuthContext } from "../../../../context/AuthContext";
import Table from "../../../ui/Table";
import Loader from "../../../ui/Loader";
import { swalError } from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const statusOptions = [
  { value: "Pending", label: "Pending", icon: "⏳" },
  { value: "Requested", label: "Requested", icon: "📨" },
  { value: "Review", label: "Review", icon: "🔍" },
  { value: "Graded", label: "Graded", icon: "✅" },
];

const summaryStatusOptions = [
  { value: "", label: "All", icon: "📋" },
  { value: "Pending", label: "Pending", icon: "⏳" },
  { value: "Requested", label: "Requested", icon: "📨" },
  { value: "Summarized", label: "Summarized", icon: "✅" },
];

export default function GetAllAnswerPapers() {
  const { id } = useParams();
  const isGlobal = id === "all";
  const { isAuthenticated, primaryRole } = useContext(AuthContext);
  const [institute, setInstitute] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("Requested");
  const [selectedSummaryStatus, setSelectedSummaryStatus] = useState("");

  const breadcrumbs = [
    { name: "Home", path: "/superadmin" },
    {
      name: "Answer Papers",
      path: `/superadmin/answer-papers/${id}`,
      isLast: true,
    },
    {
      name: institute?.name || (isGlobal ? "All Institutes" : ""),
      path: `/superadmin/answer-papers/${id}`,
      isLast: true,
    },
  ];

  const fetchInstituteDetails = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get/${id}`,
        { credentials: "include" },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch institute details");
      const data = await response.json();
      setInstitute(data);
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  const fetchAnswerPapers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (!isGlobal) params.append("instituteId", id);
      params.append("status", selectedStatus);
      if (selectedSummaryStatus)
        params.append("summaryStatus", selectedSummaryStatus);

      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get-all-answerpapers?${params.toString()}`,
        { credentials: "include" },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch answer papers");

      const result = await response.json();
      setData(result.data || []);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, isGlobal, selectedStatus, selectedSummaryStatus]);

  useEffect(() => {
    if (!isGlobal) {
      fetchInstituteDetails();
    }
  }, [fetchInstituteDetails, isGlobal]);

  useEffect(() => {
    fetchAnswerPapers();
  }, [fetchAnswerPapers]);

  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/login" replace />;
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Pending":
        return "badge-pending";
      case "Requested":
        return "badge-requested";
      case "Review":
        return "badge-review";
      case "Graded":
        return "badge-graded";
      case "Summarized":
        return "badge-graded";
      default:
        return "badge-default";
    }
  };

  const columns = [
    {
      key: "instituteName",
      label: "Institute",
      sortable: true,
      filter: true,
    },
    {
      key: "className",
      label: "Class",
      sortable: true,
    },
    {
      key: "testName",
      label: "Test",
      sortable: true,
    },
    {
      key: "studentName",
      label: "Student",
      sortable: true,
    },
    {
      key: "_id",
      label: "Answer Paper ID",
      sortable: true,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => (
        <span className={`status-badge ${getStatusBadgeClass(row.status)}`}>
          {row.status}
        </span>
      ),
    },
    {
      key: "summaryStatus",
      label: "Summary Status",
      sortable: true,
      render: (row) => (
        <span
          className={`status-badge ${getStatusBadgeClass(
            row.summaryStatus || "Pending",
          )}`}
        >
          {row.summaryStatus || "N/A"}
        </span>
      ),
    },
    {
      key: "filePageCount",
      label: "Pages",
      sortable: true,
      render: (row) => <span>{row.filePageCount || 0}</span>,
    },
    {
      key: "scoringTryCount",
      label: "Scoring Tries",
      sortable: true,
    },
    {
      key: "summaryTryCount",
      label: "Summary Tries",
      sortable: true,
    },
    {
      key: "createdAt",
      label: "Created At",
      sortable: true,
      render: (row) => (
        <span>
          {row.createdAt ? new Date(row.createdAt).toLocaleString() : "N/A"}
        </span>
      ),
    },
    {
      key: "updatedAt",
      label: "Updated At",
      sortable: true,
      render: (row) => (
        <span>
          {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "N/A"}
        </span>
      ),
    },
  ];

  return (
    <>
      <Navbar
        pageName={`Answer Papers${institute ? `: ${institute.name}` : ""}`}
        activePage="Answer Papers"
        breadcrumbs={breadcrumbs}
      />
      <div className="manage-token">
        <div className="controls-section">
          <div className="type-selector">
            <label>Status:</label>
            <div className="type-buttons">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  className={`type-btn ${
                    selectedStatus === option.value ? "active" : ""
                  }`}
                  onClick={() => setSelectedStatus(option.value)}
                >
                  <span className="type-icon">{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="type-selector" style={{ marginTop: "12px" }}>
            <label>Summary Status:</label>
            <div className="type-buttons">
              {summaryStatusOptions.map((option) => (
                <button
                  key={option.value}
                  className={`type-btn ${
                    selectedSummaryStatus === option.value ? "active" : ""
                  }`}
                  onClick={() => setSelectedSummaryStatus(option.value)}
                >
                  <span className="type-icon">{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="stats-grid" style={{ marginTop: "16px" }}>
          <div className="stat-card">
            <div className="stat-icon total">📄</div>
            <div className="stat-content">
              <h3>Total Papers</h3>
              <p className="stat-number">{data.length}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon input">⏳</div>
            <div className="stat-content">
              <h3>Pending</h3>
              <p className="stat-number">
                {data.filter((d) => d.status === "Pending").length}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon output">📨</div>
            <div className="stat-content">
              <h3>Requested</h3>
              <p className="stat-number">
                {data.filter((d) => d.status === "Requested").length}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon cost">✅</div>
            <div className="stat-content">
              <h3>Graded</h3>
              <p className="stat-number">
                {data.filter((d) => d.status === "Graded").length}
              </p>
            </div>
          </div>
        </div>

        <div className="table-section" style={{ marginTop: "16px" }}>
          {loading ? (
            <Loader style={{ fontSize: "2.2rem" }} />
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">⚠️</div>
              <h2>Error Loading Data</h2>
              <p>{error}</p>
              <button onClick={fetchAnswerPapers} className="retry-btn">
                <span>🔄</span> Retry
              </button>
            </div>
          ) : (
            <Table
              data={data}
              columns={columns}
              caption={`Answer Papers (${data.length})`}
            />
          )}
        </div>
      </div>
    </>
  );
}
